import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3, Users, BedDouble, AlertTriangle, Circle, User, Target,
  PieChart as PieChartIcon, TrendingUp, UserPlus,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useSites } from '@/hooks/useSites';
import { useRooms } from '@/hooks/useRooms';
import { Alert, Client } from '@/types/shelter';
import { DistributionTotalsCard } from '@/components/analytics/DistributionTotalsCard';

interface OperationsAnalyticsCenterProps {
  clients: Client[];
  alerts: Alert[];
  totalCapacity: number;
  checkedInCount: number;
  dailyRosterCount: number;
  userName: string;
}

const MetricCard = ({
  icon: Icon, label, value, suffix, subLabel,
  gradient, iconColor,
}: {
  icon: any; label: string; value: number | string; suffix?: string;
  subLabel?: string; gradient: string; iconColor: string;
}) => (
  <Card className={`relative overflow-hidden border-2 ${gradient}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconColor}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <span className="text-sm font-semibold">{label}</span>
            {subLabel && (
              <div className="text-xs opacity-70 mt-0.5">{subLabel}</div>
            )}
          </div>
        </div>
        <div className="text-right">
          <span className="text-3xl font-bold">{value}{suffix || ''}</span>
        </div>
      </div>
    </CardContent>
  </Card>
);

const OperationsAnalyticsCenter: React.FC<OperationsAnalyticsCenterProps> = ({
  clients, alerts, totalCapacity, checkedInCount, dailyRosterCount,
}) => {
  const { sites = [] } = useSites();
  const { rooms = [] } = useRooms();

  // Real derivations from props
  const activeClients = clients.length;
  const checkedIn = clients.filter(c => c.status === 'checked-in').length;
  const checkedOut = clients.filter(c => c.status === 'checked-out').length;
  const unbedded = clients.filter(c => c.status === 'unbedded').length;
  const waitlist = clients.filter(c => c.status === 'waitlist').length;
  const reserved = clients.filter(c => c.status === 'reserved').length;
  const returnees = clients.filter(c => c.isReturnee).length;
  const occupancyRate = totalCapacity > 0 ? (checkedInCount / totalCapacity * 100) : 0;
  const todayStr = new Date().toDateString();
  const newIntakes = clients.filter((c: any) =>
    !c.archived_at && c.created_at && new Date(c.created_at).toDateString() === todayStr
  ).length;

  const activeAlerts = alerts.filter(a => a.status === 'active');
  const criticalCount = activeAlerts.filter(a => a.type === 'critical').length;
  const warningCount = activeAlerts.filter(a => a.type === 'warning').length;
  const infoCount = activeAlerts.filter(a => a.type === 'info').length;

  const alertsByType = useMemo(() => [
    { name: 'Critical', value: criticalCount, color: '#ef4444' },
    { name: 'Warning', value: warningCount, color: '#f59e0b' },
    { name: 'Info', value: infoCount, color: '#3b82f6' },
  ], [criticalCount, warningCount, infoCount]);

  const statusBreakdown = useMemo(() => {
    const items = [
      { name: 'Checked In', value: checkedIn, color: '#10b981' },
      { name: 'Checked Out', value: checkedOut, color: '#f59e0b' },
      { name: 'Unbedded', value: unbedded, color: '#94a3b8' },
      { name: 'Waitlist', value: waitlist, color: '#8b5cf6' },
      { name: 'Reserved', value: reserved, color: '#3b82f6' },
    ].filter(s => s.value > 0);
    return items;
  }, [checkedIn, checkedOut, unbedded, waitlist, reserved]);

  const facilityOccupancy = useMemo(() => {
    const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];
    return sites.map((site, i) => {
      const siteRooms = rooms.filter((r: any) => r.site_id === site.id);
      const cap = siteRooms.reduce((a: number, r: any) => a + (r.capacity || 0), 0);
      const occ = clients.filter(c => c.site_id === site.id && c.room_id && c.status !== 'not_present' && c.status !== 'waitlist').length;
      const pct = cap > 0 ? Math.round((occ / cap) * 100) : 0;
      return { name: site.name, occupancy: pct, occ, cap, fill: colors[i % colors.length] };
    });
  }, [sites, rooms, clients]);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Live indicator */}
      <div className="flex items-center justify-end gap-2">
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <Circle className="h-2 w-2 mr-1 fill-green-500 text-green-500" />
          Live
        </Badge>
        <span className="text-xs text-muted-foreground">Updated {new Date().toLocaleTimeString()}</span>
      </div>

      {/* Real KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          icon={BedDouble} label="Currently Bedded" value={checkedInCount}
          suffix={totalCapacity > 0 ? `/${totalCapacity}` : ''}
          subLabel={totalCapacity > 0 ? `${occupancyRate.toFixed(0)}% capacity` : 'No capacity set'}
          gradient="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200 text-blue-900"
          iconColor="bg-blue-100 text-blue-600"
        />
        <MetricCard
          icon={AlertTriangle} label="Active Alerts" value={activeAlerts.length}
          subLabel={`${criticalCount} critical, ${warningCount} warning`}
          gradient="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200 text-amber-900"
          iconColor="bg-amber-100 text-amber-600"
        />
        <MetricCard
          icon={TrendingUp} label="Returnees" value={returnees}
          subLabel="Clients returning to shelter"
          gradient="bg-gradient-to-br from-cyan-50 to-cyan-100/50 border-cyan-200 text-cyan-900"
          iconColor="bg-cyan-100 text-cyan-600"
        />
        <MetricCard
          icon={Users} label="Waitlist" value={waitlist}
          subLabel="Awaiting bed assignment"
          gradient="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200 text-purple-900"
          iconColor="bg-purple-100 text-purple-600"
        />
        <MetricCard
          icon={UserPlus} label="New Intakes" value={newIntakes}
          subLabel="Admitted today"
          gradient="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200 text-emerald-900"
          iconColor="bg-emerald-100 text-emerald-600"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PieChartIcon className="h-4 w-4 text-purple-600" />
              Client Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusBreakdown.length === 0 ? (
              <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">
                No active clients
              </div>
            ) : (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie
                      data={statusBreakdown}
                      cx="50%" cy="50%"
                      innerRadius={50} outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {statusBreakdown.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3 flex-1">
                  {statusBreakdown.map(s => (
                    <div key={s.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-sm">{s.name}</span>
                      </div>
                      <span className="text-sm font-semibold">{s.value}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Total</span>
                      <span className="text-sm font-bold">{activeClients}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alert Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Active Alert Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeAlerts.length === 0 ? (
              <div className="h-[180px] flex items-center justify-center text-sm text-muted-foreground">
                No active alerts
              </div>
            ) : (
              <div className="space-y-4">
                {alertsByType.map((alert, idx) => {
                  const max = Math.max(...alertsByType.map(a => a.value), 1);
                  return (
                    <div key={alert.name} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: alert.color }} />
                          <span className="font-medium">{alert.name}</span>
                        </div>
                        <span className="font-semibold">{alert.value}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            backgroundColor: alert.color,
                            width: `${(alert.value / max) * 100}%`,
                            transitionDelay: `${idx * 150}ms`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Facility Occupancy Comparison */}
        {facilityOccupancy.length > 0 && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Facility Occupancy Comparison
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {facilityOccupancy.map((fac, idx) => (
                  <div key={fac.name} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{fac.name}</span>
                      <span className="font-semibold">
                        {fac.occ}/{fac.cap} ({fac.occupancy}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          backgroundColor: fac.fill,
                          width: `${Math.min(fac.occupancy, 100)}%`,
                          transitionDelay: `${idx * 100}ms`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Distribution Totals from Shift Reports (real data) */}
      <DistributionTotalsCard />
    </div>
  );
};

export default OperationsAnalyticsCenter;
